/* eslint-disable no-shadow */
import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import TextField from "../../shared/publicForms/TextField";
import CheckboxWithTermsModal from "../../shared/publicForms/CheckboxWithTermsModal";
import loader from "../../shared/loaders/img/spinner-white.gif";
import { submitForm } from "../../../actions/portalAgencySignUpActions";
import ServerErrorMessage from "../../shared/general/ServerErrorMessage";
import EmailErrorMessage from "../../shared/general/EmailErrorMessage";
import {
  StepWrapper,
  Title,
  DisabledButton,
  Button,
  Spinner,
  ReCaptchaContainer,
  ReCaptchaError,
} from "../styles/portal-agency-sign-up-styles";

export interface IPersonalDetailsProps {
  submitForm: (data: Record<string, unknown>) => Record<string, unknown>;
  formLoading: boolean;
  serverError?: boolean;
  emailError?: boolean;
}

const initialValues = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  companyName: "",
  recaptcha: "3000",
  termsAgreed: false,
};

const validationSchema = yup.object({
  firstName: yup
    .string()
    .trim()
    .matches(/^[A-zÀ-ÿ\s]*$/, "First name can only include letters")
    .min(2)
    .max(20)
    .required()
    .label("First name"),
  lastName: yup
    .string()
    .trim()
    .matches(/^[A-zÀ-ÿ\s]*$/, "Last name can only include letters")
    .min(2)
    .max(20)
    .required()
    .label("Last name"),
  email: yup.string().email().min(5).max(50).required().label("Email"),
  phone: yup.string().min(8).max(14).required().label("Phone number"),
  companyName: yup
    .string()
    .min(2)
    .max(30)
    .trim()
    .required()
    .label("Company name"),
  recaptcha: yup.string().nullable().required("This field is required"),
  termsAgreed: yup.boolean().oneOf([true], "You must accept Terms of Business"),
});

// const recaptchaKey = process.env.RECAPTCHA_SITE_KEY || "";

export const PersonalDetails: FunctionComponent<IPersonalDetailsProps> = ({
  submitForm,
  formLoading,
  serverError,
  emailError,
}) => (
  <StepWrapper data-test-id="personal-details-step" formStep>
    <Title data-test-id="title">Your Details</Title>
    {serverError && <ServerErrorMessage />}
    {emailError && <EmailErrorMessage />}
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(data) => {
        submitForm(data);
      }}
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form autoComplete="off">
          <Field
            disabled={formLoading || serverError}
            name="firstName"
            type="text"
            minLength={2}
            maxLength={21}
            placeholder="First Name"
            isError={errors.firstName && touched.firstName}
            aria-label="First Name"
            as={TextField}
          />
          <Field
            disabled={formLoading || serverError}
            name="lastName"
            type="text"
            minLength={2}
            maxLength={21}
            placeholder="Last Name"
            isError={errors.lastName && touched.lastName}
            aria-label="Last Name"
            as={TextField}
          />
          <Field
            disabled={formLoading || serverError}
            name="phone"
            type="number"
            placeholder="Contact Number"
            isError={errors.phone && touched.phone}
            aria-label="Contact Number"
            as={TextField}
          />
          <Field
            disabled={formLoading || serverError}
            name="email"
            type="email"
            minLength={5}
            maxLength={51}
            placeholder="Email"
            isError={errors.email && touched.email}
            aria-label="Email"
            as={TextField}
          />
          <Field
            disabled={formLoading || serverError}
            name="companyName"
            type="text"
            minLength={2}
            maxLength={31}
            placeholder="Company Name"
            isError={errors.companyName && touched.companyName}
            aria-label="Company Name"
            as={TextField}
          />
          <Field
            disabled={formLoading || serverError}
            name="termsAgreed"
            type="checkbox"
            isError={errors.termsAgreed && touched.termsAgreed}
            aria-label="By submitting this request, you are agreeing to be bound by our terms of business"
            labelText="By submitting this request, you are agreeing to be bound by our "
            modalTriggerName="terms of business"
            privacyPolicy
            as={CheckboxWithTermsModal}
          />
          {/*<ReCaptchaContainer disabled={formLoading || serverError}>*/}
          {/*  <ReCAPTCHA*/}
          {/*    sitekey={recaptchaKey}*/}
          {/*    onChange={(response: string) => {*/}
          {/*      setFieldValue("recaptcha", response);*/}
          {/*    }}*/}
          {/*  />*/}
          {/*  {errors.recaptcha && touched.recaptcha && (*/}
          {/*    <ReCaptchaError>{errors.recaptcha}</ReCaptchaError>*/}
          {/*  )}*/}
          {/*</ReCaptchaContainer>*/}
          {!values.termsAgreed ||
          formLoading ||
          serverError ? (
            <DisabledButton type="button" data-test-id="disabled-button">
              {formLoading ? (
                <Spinner src={loader as string} alt="Spinner" />
              ) : (
                "submit"
              )}
            </DisabledButton>
          ) : (
            <Button type="submit" data-test-id="submit-button">
              submit
            </Button>
          )}
        </Form>
      )}
    </Formik>
  </StepWrapper>
);

PersonalDetails.propTypes = {
  submitForm: PropTypes.func.isRequired,
  formLoading: PropTypes.bool.isRequired,
  serverError: PropTypes.bool.isRequired,
  emailError: PropTypes.bool.isRequired,
};

interface IReduxState {
  portalAgencySignUp: {
    formLoading: boolean;
    serverError: boolean;
    emailError: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  formLoading: state.portalAgencySignUp.formLoading,
  serverError: state.portalAgencySignUp.serverError,
  emailError: state.portalAgencySignUp.emailError,
});

export default connect(mapStateToProps, { submitForm })(PersonalDetails);
